import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  apiBaseUrl,
  AuthApi,
  cleanupAuthStoredTokenData,
  FORCE_LOGIN_QUERY_PARAM,
  FORCE_LOGIN_QUERY_VALUE,
  getBaseUrl,
  I18N_PUBLIC_NAMESPACE,
  i18nInitializedInstance,
  IApiError,
  ILoginCredentials,
  isLoggedInAtom,
  isLoginInProgressAtom,
  isMfaLoginResponse,
  isSessionGateLoaderAtom,
  Log,
  LoginAuthType,
  loginErrorsAtom,
  loginFlowStepAtom,
  LoginFormFlow,
  mfaChallengeTokenAtom,
  normalizeErrors,
  ProfileStorage,
  RoutesApiConst,
  SimpleResult,
  simpleResultFalse,
  simpleResultTrue,
  storeCurrentPageAndRedirectToLogin,
  updateTokensAndReconnectSocket,
} from "@ryde/shared";

import { useProfileActions } from "../../hooks";
import { useSimpleNavigation } from "../../navigation";

export const useAuthActions = () => {
  const { t } = useTranslation();
  const setLoginErrors = useSetRecoilState(loginErrorsAtom);
  const setLoggingInProgress = useSetRecoilState(isLoginInProgressAtom);
  const setIsLoggedIn = useSetRecoilState(isLoggedInAtom);
  const setIsSessionGateLoader = useSetRecoilState(isSessionGateLoaderAtom);
  const setLoginFlowStep = useSetRecoilState(loginFlowStepAtom);
  const setMfaChallengeToken = useSetRecoilState(mfaChallengeTokenAtom);
  const { navigateToHomeOrLoginReturnUrl, navigateLogin } =
    useSimpleNavigation();
  const { clearProfileRecoilStore } = useProfileActions();

  const handleUserLogin = useCallback(
    async (credentials: ILoginCredentials): Promise<void> => {
      try {
        setLoginErrors([]);
        setLoggingInProgress(true);
        const loginResult = await AuthApi.sessionLogin({
          username: credentials[LoginAuthType.Username],
          email: credentials[LoginAuthType.Email],
          phone: credentials[LoginAuthType.Phone],
          password: credentials.password,
          recaptchaToken: credentials.recaptchaToken,
        });

        if (!loginResult?.success || !loginResult.data) {
          const errorsNormalized = normalizeErrors(loginResult?.error, t);
          setLoginErrors(errorsNormalized);
          return;
        }

        if (isMfaLoginResponse(loginResult.data)) {
          Log.debug("MFA challenge issued, switching to MFA step");
          setMfaChallengeToken(loginResult.data.mfa_challenge_token);
          setLoginFlowStep(LoginFormFlow.MFA_STEP);
          return;
        }

        const profile = loginResult.data;

        Log.debug(
          "Session established for: ",
          credentials[LoginAuthType.Username],
          profile,
        );

        ProfileStorage.prepareScopeForUserLogin(profile.id);

        updateTokensAndReconnectSocket({
          accessToken: null,
          refreshToken: null,
        });
        setIsLoggedIn(true);
        navigateToHomeOrLoginReturnUrl();
      } catch (error) {
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        setLoginErrors(errorsNormalized);
        Log.warnAndToast(t("errors.authenticationError"), error);
      } finally {
        setLoggingInProgress(false);
      }
    },
    [
      navigateToHomeOrLoginReturnUrl,
      setIsLoggedIn,
      setLoggingInProgress,
      setLoginErrors,
      setLoginFlowStep,
      setMfaChallengeToken,
      t,
    ],
  );

  const handleForgotPassword = useCallback(
    async (email: string): Promise<void> => {
      try {
        setLoginErrors([]);
        await AuthApi.forgotPassword({
          email,
          redirectUrl: `${getBaseUrl()}/#/reset-password`,
        });
        Log.successAndToast(t("login.PASSWORD_RESET_LINK_SENT"));
      } catch (error) {
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        setLoginErrors(errorsNormalized);
        Log.warnAndToast(t("errors.forgotPasswordError"), error);
      }
    },
    [setLoginErrors, t],
  );

  const handleResetPassword = useEvent(
    async ({
      token,
      password,
      recaptchaToken,
    }: {
      token: string;
      password: string;
      recaptchaToken: string;
    }): Promise<SimpleResult<void>> => {
      try {
        setLoginErrors([]);
        await AuthApi.resetPassword({ token, password, recaptchaToken });
        Log.successAndToast(t("login.PASSWORD_RESET_SUCCESSFULLY"));
        return simpleResultTrue;
      } catch (error) {
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        setLoginErrors(errorsNormalized);
        Log.warnAndToast(t("errors.resetPasswordError"), error);
        return simpleResultFalse;
      }
    },
  );

  const handleLogout = useEvent(
    async (keepReturnUrl = false): Promise<void> => {
      Log.debug(`Logout handler called`);
      try {
        await AuthApi.sessionLogout();
      } catch {
        Log.debug("Session logout API call failed");
      }
      // Login and always-mounted shells must bind to `public` before `general` is dropped,
      // otherwise react-i18next suspends the root bootstrap spinner over /login.
      i18nInitializedInstance.setDefaultNamespace(I18N_PUBLIC_NAMESPACE);
      clearProfileRecoilStore();
      cleanupAuthStoredTokenData();
      setIsLoggedIn(false);
      setIsSessionGateLoader(false);

      if (!keepReturnUrl) {
        navigateLogin();
      } else {
        storeCurrentPageAndRedirectToLogin();
      }
    },
  );

  const handleSamlLogin = useEvent(
    async ({
      emailDomain,
      companyCode,
      forceLogin,
    }: {
      emailDomain?: string;
      companyCode?: string;
      forceLogin?: boolean;
    }): Promise<void> => {
      const loginUrl = new URL(RoutesApiConst.auth.loginSaml, apiBaseUrl());

      if (emailDomain) {
        loginUrl.searchParams.append("emailDomain", emailDomain);
      }

      if (companyCode) {
        loginUrl.searchParams.append("companyCode", companyCode);
      }

      loginUrl.searchParams.append(
        "redirectUrl",
        encodeURIComponent(`${getBaseUrl()}/#/login`),
      );

      if (forceLogin) {
        // OAuth consent freshness (RYDE-7833): backend sets SAML ForceAuthn=true so the IdP
        // reauthenticates and the AuthnInstant advances past the request time.
        loginUrl.searchParams.append(
          FORCE_LOGIN_QUERY_PARAM,
          FORCE_LOGIN_QUERY_VALUE,
        );
      }

      window.location.href = loginUrl.href;
    },
  );

  const handleGoogleLogin = useEvent(
    async (options?: { forceLogin?: boolean }): Promise<void> => {
      const loginUrl = new URL(RoutesApiConst.auth.loginGoogle, apiBaseUrl());

      loginUrl.searchParams.append(
        "state",
        encodeURIComponent(`${getBaseUrl()}/#/login`),
      );

      if (options?.forceLogin) {
        // OAuth consent freshness (RYDE-7833): the backend guard maps forceLogin → prompt=login,
        // so Google reauthenticates and the id_token's auth_time proves the fresh login.
        loginUrl.searchParams.append(
          FORCE_LOGIN_QUERY_PARAM,
          FORCE_LOGIN_QUERY_VALUE,
        );
      }

      window.location.href = loginUrl.href;
    },
  );

  return {
    handleUserLogin,
    handleForgotPassword,
    handleResetPassword,
    handleLogout,
    handleSamlLogin,
    handleGoogleLogin,
    setLoginErrors,
  };
};
